import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listDispatches, updateWorkOrder } from "@/lib/api";
import { addBusinessDays } from "./workOrderTabUtils";

// Title / Site / Date / Scheduled / Completion 5-column row at the top
// of the WO form. The Scheduled-date input carries the most logic —
// it auto-flips status to "scheduled" when a date is entered AND
// auto-calculates the completion date based on remaining labor hours
// across line items + approved COs (subtracting any already-logged
// dispatch hours).  Extracted from WorkOrderTab.jsx (Feb 2026, iter178).
export function WODatesRow({
  form, setForm,
  isEditable,
  clientSites,
  scheduledDateRef,
  editingCompletionDate, setEditingCompletionDate,
  editingWO, setEditingWO,
  clientId,
  subtotal, taxAmount, total,
  loadWorkOrders,
  siteOptional = false,
  stacked = false,
}) {
  // Scheduled-date change handler. Lives in this component because it
  // mutates state owned by the parent (form, editingWO, etc.) via the
  // setters we already accept. Keeps the JSX above terse.
  const onScheduledChange = async (e) => {
    const newDate = e.target.value;
    const shouldSchedule = newDate && ["draft", "submitted", "approved"].includes(form.status);
    let autoCompletion = form.completion_date;
    if (newDate) {
      try {
        const totalLaborHours = (form.line_items || []).reduce((sum, li) => {
          const isLabor = String(li.cost_type || "") === "1";
          return sum + (isLabor ? (parseFloat(li.quantity) || 0) : 0);
        }, 0);
        const coLaborHours = (form.change_orders || [])
          .filter((co) => co.status === "approved")
          .reduce((sum, co) => sum + (co.line_items || []).reduce((s, li) => {
            const isLabor = String(li.cost_type || "") === "1" || String(li.cost_type || "").toLowerCase() === "labor";
            return s + (isLabor ? (parseFloat(li.quantity) || 0) : 0);
          }, 0), 0);
        const budgetedHours = totalLaborHours + coLaborHours;
        let loggedHours = 0;
        if (editingWO?.id) {
          try {
            const disp = await listDispatches({ wo_id: editingWO.id });
            loggedHours = (disp.data || []).reduce((s, d) => s + (parseFloat(d.logged_hours) || 0), 0);
          } catch { /* ignore */ }
        }
        const remainingHours = Math.max(0, budgetedHours - loggedHours);
        const techCount = Math.max(1, (form.assigned_tech_ids || []).filter(Boolean).length);
        if (remainingHours > 0) {
          const days = Math.max(1, Math.ceil(remainingHours / techCount / 10));
          autoCompletion = addBusinessDays(newDate, days - 1);
        } else {
          // No remaining labor hours (either fully logged, or zero
          // labor budgeted at all — e.g. inspection-only WOs).
          // Default completion to the scheduled day itself so the
          // field doesn't stay blank and force manual entry.
          autoCompletion = newDate;
        }
      } catch { /* silent fail — keep existing completion_date */ }
    }
    setForm((f) => ({
      ...f,
      scheduled_date: newDate,
      status: shouldSchedule ? "scheduled" : f.status,
      completion_date: autoCompletion,
    }));
    if (editingWO && newDate) {
      try {
        const payload = {
          ...form, scheduled_date: newDate,
          completion_date: autoCompletion,
          status: shouldSchedule ? "scheduled" : form.status,
          client_id: clientId, subtotal, tax_amount: taxAmount, total,
          line_items: form.line_items.map((li) => ({ ...li, total: li.quantity * li.unit_price })),
        };
        const res = await updateWorkOrder(editingWO.id, payload);
        setEditingWO(res.data);
        const recalcd = autoCompletion && autoCompletion !== form.completion_date;
        toast.success(shouldSchedule
          ? (recalcd ? `Scheduled — completion auto-set to ${autoCompletion}` : "Work Order scheduled")
          : (recalcd ? `Scheduled date saved — completion updated to ${autoCompletion}` : "Scheduled date saved"));
        loadWorkOrders();
      } catch { toast.error("Failed to save scheduled date"); }
    }
  };

  const onCompletionChange = async (e) => {
    const newDate = e.target.value;
    setForm((f) => ({ ...f, completion_date: newDate }));
    if (newDate) setEditingCompletionDate(false);
    if (editingWO && newDate) {
      try {
        const payload = {
          ...form, completion_date: newDate, client_id: clientId,
          subtotal, tax_amount: taxAmount, total,
          line_items: form.line_items.map((li) => ({ ...li, total: li.quantity * li.unit_price })),
        };
        const res = await updateWorkOrder(editingWO.id, payload);
        setEditingWO(res.data);
        toast.success("Completion date saved");
        loadWorkOrders();
      } catch { toast.error("Failed to save completion date"); }
    }
  };

  // `stacked` = inside the 1/3-width General card: Title and Site take the full row, the three dates share one.
  return (
    <div className={`grid gap-3 ${stacked ? "grid-cols-3" : "grid-cols-1 md:grid-cols-5"}`} data-testid="wo-dates-row">
      <div className={stacked ? "col-span-3" : ""}>
        <Label className="text-xs">Title</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Work order title"
          data-testid="input-wo-title"
          disabled={!isEditable}
        />
      </div>
      <div className={stacked ? "col-span-3" : ""}>
        <Label className="text-xs">Site {!siteOptional && <span className="text-red-500">*</span>}</Label>
        <Select
          value={form.site_id || "none"}
          onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? "" : v })}
          disabled={!isEditable}
        >
          <SelectTrigger
            data-testid="select-wo-site"
            className={!form.site_id && !siteOptional ? "border-red-300" : ""}
          >
            <SelectValue placeholder="Select site" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-</SelectItem>
            {clientSites.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Date</Label>
        <Input
          type="date"
          value={form.work_date}
          onChange={(e) => setForm({ ...form, work_date: e.target.value })}
          data-testid="input-wo-date"
          disabled={!isEditable}
        />
      </div>
      <div>
        <Label className="text-xs">Scheduled</Label>
        <Input
          ref={scheduledDateRef}
          type="date"
          value={form.scheduled_date}
          onChange={onScheduledChange}
          data-testid="input-wo-scheduled"
          disabled={form.status === "completed" || form.status === "cancelled"}
        />
      </div>
      <div>
        <Label className="text-xs">Completion Date</Label>
        {form.completion_date && !editingCompletionDate ? (
          <div
            className="h-9 px-3 flex items-center text-sm bg-slate-50 text-slate-600 border rounded-md cursor-pointer hover:bg-slate-100 transition-colors"
            onClick={() => setEditingCompletionDate(true)}
            data-testid="completion-date-display"
          >
            {form.completion_date}
          </div>
        ) : (
          <Input
            type="date"
            value={form.completion_date}
            onChange={onCompletionChange}
            data-testid="input-wo-completion-date"
          />
        )}
      </div>
    </div>
  );
}
